import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { getApiUrl } from "../utils";
import { getCacheKey, getCachedData, setCachedData, isCacheValid, clearCache, CACHEABLE_URLS, DEFAULT_CACHE_CONFIG, CacheConfig } from '../services/cacheService';

interface RequestOptions {
  url: string;
  data?: any;
  params?: any;
  extraHeaders?: Record<string, string>;
  cache?: Partial<CacheConfig> & {
    forceRefresh?: boolean;
  };
}

class AxiosClient {
  protected axiosInstance: AxiosInstance;

  constructor() {
    // const devUrl = process.env.REACT_APP_BACKEND_LOCAL;
    // const prodUrl = process.env.REACT_APP_PHP_PROD;
    const prodUrl = getApiUrl();


    this.axiosInstance = axios.create({
      baseURL: prodUrl,
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        'appkey': process.env.REACT_APP_APP_KEY,
      },
    });

    // Add a request interceptor
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem("U_EVAL_VALUE") || Cookies.get("U_EVAL_VALUE");

        config.headers.Authorization = `Bearer ${token}`;

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        // Handle response error here
        return Promise.reject(error);
      }
    );
  }

  async get<T>({ url, data = {}, params = {}, extraHeaders = {}, cache = {} }: RequestOptions): Promise<T> {
    try {
      const cacheKey = getCacheKey(url, params);
      const urlConfig = CACHEABLE_URLS[url as keyof typeof CACHEABLE_URLS] ?? DEFAULT_CACHE_CONFIG;
      
      const { 
        enabled = urlConfig.enabled, 
        duration = urlConfig.duration,
        forceRefresh = false 
      } = cache;

      // Check cache if enabled and not force refreshing
      if (enabled && !forceRefresh) {
        const cachedData = getCachedData<T>(cacheKey);
        if (cachedData && isCacheValid(cacheKey)) {
          return cachedData.data;
        }
      }

      // Proceed with the API call
      const config: AxiosRequestConfig = {
        url,
        params,
        headers: {
          ...this.axiosInstance.defaults.headers.common,
          ...extraHeaders,
        },
        data,
        method: "get",
      };

      const response: AxiosResponse<T> = await this.axiosInstance.request(config);

      // Cache the response if caching is enabled for this URL
      if (enabled) {
        setCachedData(cacheKey, response.data, duration);
      }

      return response.data;
    } catch (error) {
      this.handleError(error);
      return {} as T; // This line should be unreachable, but TypeScript needs it
    }
  }

  async post<T>({ url, data = {}, extraHeaders = {}, cache = {} }: RequestOptions): Promise<T> {
    try {
      const urlConfig = CACHEABLE_URLS[url as keyof typeof CACHEABLE_URLS] ?? DEFAULT_CACHE_CONFIG;
      const { 
        enabled = urlConfig.enabled, 
        duration = urlConfig.duration,
        forceRefresh = false 
      } = cache;

      // Check cache if enabled and not force refreshing
      if (enabled && !forceRefresh) {
        const cacheKey = getCacheKey(url, data);  // Include POST data in cache key
        const cachedData = getCachedData<T>(cacheKey);
        if (cachedData && isCacheValid(cacheKey)) {
          return cachedData.data;
        }
      }

      const config: AxiosRequestConfig = {
        headers: {
          ...this.axiosInstance.defaults.headers.common,
          ...extraHeaders,
        },
      };

      const response: AxiosResponse<T> = await this.axiosInstance.post(url, data, config);

      // Cache the response if enabled
      if (enabled) {
        const cacheKey = getCacheKey(url, data);
        setCachedData(cacheKey, response.data, duration);
      }

      return response.data;
    } catch (error) {
      this.handleError(error);
      return {} as T; // This line should be unreachable, but TypeScript needs it
    }
  }

  async put<T>({ url, data = {}, extraHeaders = {} }: RequestOptions): Promise<T> {
    try {
      const config: AxiosRequestConfig = {
        headers: {
          ...this.axiosInstance.defaults.headers.common,
          ...extraHeaders,
        },
      };

      const response: AxiosResponse<T> = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      return {} as T; // This line should be unreachable, but TypeScript needs it
    }
  }

  async delete<T>({ url, extraHeaders = {} }: RequestOptions): Promise<T> {
    try {
      const config: AxiosRequestConfig = {
        headers: {
          ...this.axiosInstance.defaults.headers.common,
          ...extraHeaders,
        },
      };

      const response: AxiosResponse<T> = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      return {} as T; // This line should be unreachable, but TypeScript needs it
    }
  }

  // protected handleError(error: any): never {
  //   if (axios.isAxiosError(error)) {
  //     if (error.response) {
  //       // The request was made and the server responded with a status code
  //       // that falls out of the range of 2xx
  //       throw new Error(`Error: ${error.response.status}, ${error.response.data}`);
  //     } else if (error.request) {
  //       // The request was made but no response was received
  //       throw new Error("No response received from the server");
  //     } else {
  //       // Something happened in setting up the request that triggered an Error
  //       throw new Error("Error setting up request");
  //     }
  //   } else {
  //     // Handle non-Axios errors
  //     throw new Error("An unexpected error occurred");
  //   }
  // }

  protected handleError(error: any): never {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Try to get error message string from response data
      let message = "";
 
      if (typeof error.response.data === "string") {
        message = error.response.data;
      } else if (typeof error.response.data === "object" && error.response.data !== null) {
        // Common keys where error message might reside
        message =
          error.response.data.message ||
          error.response.data.error ||
          JSON.stringify(error.response.data);
      } else {
        message = String(error.response.data);
      }

      // If backend already returned a user-facing message, surface it as-is.
      if (message) {
        throw new Error(message);
      }

      throw new Error("Request failed");
    } else if (error.request) {
      throw new Error("No response received from the server");
    } else {
      throw new Error("Error setting up request");
    }
  } else {
    throw new Error("An unexpected error occurred");
  }
}
}

export { AxiosClient };
export default new AxiosClient();
